import { Form, Modal, Table, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { useMemo, useState } from "react";
import {
  useCreateWallet,
  useDeleteWallet,
  useUpdateWallet,
} from "../../../../api/wallet";
import { ReactComponent as CloseIcon } from "../../../../common/images/svg/CloseIcon.svg";
import { ReactComponent as PencilIcon } from "../../../../common/images/svg/PencilSimpleLine.svg";
import { ReactComponent as PlusIcon } from "../../../../common/images/svg/Plus.svg";
import { ReactComponent as TrashIcon } from "../../../../common/images/svg/Trash.svg";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { SearchCustom } from "../../../../components/inputs/search/searchCustom";
import ConfirmModal from "../../../../components/modal/confirmModal";
import NextPagination from "../../../../components/pagination/next";
import PrevPagination from "../../../../components/pagination/prev";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { useWalletManagement } from "../../../../utils/hooks/useWalletManagement";
import { Row as RowTable } from "../../../components/interface";
import FormWallet from "./form-wallet";

interface IDetailInfoProps {
  tabActive: "info" | "bank" | "wallet";
  id: string | undefined;
  userId: number | undefined;
}

const start = 0;
const end = 20;

const DetailWallet = ({ tabActive, id, userId }: IDetailInfoProps) => {
  const [form] = Form.useForm();
  const { isTablet } = useResponsive();
  const [actionWallet, setActionWallet] = useState<"ADD" | "EDIT" | null>(null);
  const [walletId, setWalletId] = useState<number | null>(null);
  const [deleteWalletId, setDeleteWalletId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({ start, end });
  const [searchName, setSearchName] = useState<string>("");
  const { open: openNotification } = useNotification();

  const { dataListWallet, refetch, isLoading } = useWalletManagement(
    Number(id),
    params,
    searchName
  );

  const { mutate: createWallet, isLoading: isLoadingCreate } = useCreateWallet({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "add_wallet_success",
          message: response.data.message || "Add wallet successfully!",
          type: "success",
        });
      }
      refetch();
      setActionWallet(null);
      setWalletId(null);
      form.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "add_wallet_failure",
          message: error?.message || "Add wallet failed!",
          type: "error",
        });
      }
    },
  });

  const { mutate: updateWallet, isLoading: isLoadingUpdate } = useUpdateWallet({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "update_wallet_success",
          message: response.data.message || "Update wallet successfully!",
          type: "success",
        });
      }
      refetch();
      setActionWallet(null);
      setWalletId(null);
      form.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "update_wallet_failure",
          message: error?.message || "Update wallet failed!",
          type: "error",
        });
      }
    },
  });

  const { mutate: deleteWallet, isLoading: loadingDeleteWallet } =
    useDeleteWallet({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "delete_wallet_success",
            message: response.data.message || "Delete wallet successfully!",
            type: "success",
          });
        }
        refetch();
        setDeleteWalletId(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "delete_wallet_failure",
            message: error?.message || "Delete wallet failed!",
            type: "error",
          });
        }
      },
    });

  const handleSearch = (value: string) => {
    setSearchName(value);
    setCurrentPage(1)
    setParams({ ...params, start, end })
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setParams({
      start: (page - 1) * dataListWallet.pageSize || start,
      end: page * dataListWallet.pageSize || end,
    });
  };

  const handleCancel = () => {
    if (isLoadingCreate || isLoadingUpdate) return;
    setActionWallet(null);
    setWalletId(null);
    form.resetFields();
  };

  const onSubmitWallet = (values: any) => {
    const body = {
      companyId: Number(id),
      userId: Number(userId),
      name: values.name || "",
      assetConfigGroupId: Number(values.assetConfigGroupId) || null,
      networkId: Number(values.networkId) || null,
      address: values.address || "",
    };
    if (actionWallet === "ADD") {
      createWallet(body as any);
    } else {
      if (!walletId) return;
      updateWallet({
        id: walletId,
        ...body,
      } as any);
    }
  };

  const handleCancelDelete = () => {
    if (loadingDeleteWallet) return;
    setDeleteWalletId(null);
  };

  const handleSubmitDelete = () => {
    deleteWallet(deleteWalletId);
  };

  const columns = useMemo((): ColumnType<RowTable>[] => {
    const columns: ColumnType<RowTable>[] = [
      {
        dataIndex: "id",
        title: <span className={"column-header"}>NO.</span>,
        render: (_value, _record, index) => {
          return (
            <Typography className="text-table">
              {(dataListWallet.page - 1) * dataListWallet.pageSize + index + 1}
            </Typography>
          );
        },
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionWallet("EDIT");
            setWalletId(record.id);
          },
        }),
        width: "6%",
      },
      {
        dataIndex: "name",
        title: <span className={"column-header"}>NAME</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionWallet("EDIT");
            setWalletId(record.id);
          },
        }),
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "18.7%",
      },
      {
        dataIndex: "assetConfigGroupId",
        title: <span className={"column-header"}>CURRENCY</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionWallet("EDIT");
            setWalletId(record.id);
          },
        }),
        render: (value, data) => {
          return (
            <Typography className="text-table">
              {data.assetConfigGroup.symbol}
            </Typography>
          );
        },
        width: "13.5%",
      },
      {
        dataIndex: "networkId",
        title: <span className={"column-header"}>NETWORK</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionWallet("EDIT");
            setWalletId(record.id);
          },
        }),
        render: (value, data) => {
          return (
            <Typography className="text-table ">{data.network.name}</Typography>
          );
        },
        width: "19.5%",
      },
      {
        dataIndex: "address",
        title: <span className={"column-header"}>ADDRESS</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionWallet("EDIT");
            setWalletId(record.id);
          },
        }),
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "30%",
      },
      {
        title: <div></div>,
        render: (record: RowTable) => (
          <div className="action-table">
            <TrashIcon
              className="icon-animation"
              onClick={() => setDeleteWalletId(record.id)}
            />
            <PencilIcon
              className="icon-animation"
              onClick={() => {
                setActionWallet("EDIT");
                setWalletId(record.id);
              }}
            />
          </div>
        ),
      },
    ];
    return columns.filter(({ key }) => !key);
  }, [dataListWallet]);

  return (
    <div
      className="wallet-container"
      style={{ display: tabActive === "wallet" ? "flex" : "none" }}
    >
      <div className="wallet-header">
        <SearchCustom
          value={searchName}
          placeholder="Search"
          onChange={handleSearch}
          onSubmit={handleSearch}
        />
        <CustomButton
          size={isTablet ? "small" : "medium"}
          text="Add Wallet Address"
          icon={<PlusIcon />}
          onClick={() => {
            setActionWallet("ADD");
            setWalletId(null);
          }}
        />
      </div>
      <Table
        className={`custom-ant-table custom-ant-table-wallet ${isLoading ? "table-hidden-empty" : ""
          }`}
        rowKey="id"
        rowClassName={() => "custom-table-row"}
        style={{ tableLayout: "auto" }}
        columns={columns as any}
        dataSource={dataListWallet.items}
        loading={isLoading}
        scroll={{ y: "calc(100vh - 345px)" }}
        pagination={{
          current: currentPage,
          total: dataListWallet.total,
          pageSize: dataListWallet.pageSize,
          onChange: handlePageChange,
          itemRender: (page, type, element) => {
            switch (type) {
              case "prev":
                return (
                  <PrevPagination
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
              case "next":
                return (
                  <NextPagination
                    totalPage={dataListWallet.totalPages}
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
            }
            return element;
          },
        }}
      />
      <Modal
        open={!!actionWallet}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{
                fontSize: isTablet ? 16 : 20,
                fontWeight: 700,
                color: "#C2C6CE",
              }}
            >
              {actionWallet === "ADD"
                ? "Add Wallet Address"
                : "Edit Wallet Address"}
            </Typography>
            <CloseIcon onClick={handleCancel} className="icon-animation" />
          </>
        }
        className="edit-wallet-modal"
      >
        <FormWallet
          form={form}
          walletId={walletId}
          handleCancel={handleCancel}
          onSubmitWallet={onSubmitWallet}
          isLoading={isLoadingCreate || isLoadingUpdate}
          actionWallet={actionWallet}
        />
      </Modal>
      <ConfirmModal
        type="warning"
        title="Confirm Delete"
        description="Do you want to delete this record?"
        open={!!deleteWalletId}
        onCancel={handleCancelDelete}
        onSubmit={handleSubmitDelete}
        isLoading={loadingDeleteWallet}
      />
    </div>
  );
};

export default DetailWallet;
